<template>
  <div class="attendance-report">
    <PageSpinner :onloader="loading ? 'flex' : 'none'" />
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <router-link to="/dashboard" class="text-white">Dashboard</router-link>
      <router-link to="/school/reports-and-analytics" class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Reports and Analytics</router-link
      >
      <span class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Class Attendance Report</span
      >
    </base-header>

    <main class="container-fluid mt--7 report-shell">
      <header class="report-heading">
        <div>
          <span class="eyebrow">ACADEMICS &amp; ATTENDANCE</span>
          <h1>Class attendance</h1>
          <p>
            Understand attendance patterns and follow up where students need
            support.
          </p>
        </div>
        <div class="mode-tools">
          <span class="mode-pill" :class="mockMode ? 'demo-pill' : 'live-pill'"
            ><i class="fa" :class="mockMode ? 'fa-flask' : 'fa-database'"></i
            >{{ mockMode ? "Demonstration data" : "Live school records" }}</span
          ><button
            class="btn btn-sm btn-outline-primary"
            type="button"
            :disabled="loading"
            @click="toggleMode"
          >
            {{ mockMode ? "Return to live data" : "Preview demonstration" }}
          </button>
        </div>
      </header>

      <section class="filter-panel card border-0 shadow-sm mb-4">
        <div class="card-body">
          <div class="section-heading">
            <div>
              <h2>Report filters</h2>
              <p>
                Choose a registered term and narrow the report to a class,
                section, or student.
              </p>
            </div>
          </div>
          <div class="filter-grid">
            <div class="form-group">
              <label for="registeredTerm"
                >Registered term <span class="required">*</span
                ><span v-if="listLoading.terms" class="select-loader"
                  ><i class="select-spinner"></i>Loading</span
                ></label
              ><select
                id="registeredTerm"
                v-model="filters.RegisteredTermId"
                class="form-control"
                :disabled="listLoading.terms"
              >
                <option value="">
                  {{
                    listLoading.terms
                      ? "Loading terms…"
                      : "Select session and term"
                  }}
                </option>
                <option
                  v-for="term in terms"
                  :key="term.registeredTermId"
                  :value="String(term.registeredTermId)"
                >
                  {{ term.schSession }} · {{ term.schTerm }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="attendanceClass"
                >Class<span v-if="listLoading.classes" class="select-loader"
                  ><i class="select-spinner"></i>Loading</span
                ></label
              ><select
                id="attendanceClass"
                v-model="filters.ClassId"
                class="form-control"
                :disabled="listLoading.classes"
                @change="onClassChange"
              >
                <option value="">
                  {{
                    listLoading.classes
                      ? "Loading classes…"
                      : "All assigned classes"
                  }}
                </option>
                <option
                  v-for="item in classes"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.Sch_Class }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="attendanceOption"
                >Option<span v-if="listLoading.options" class="select-loader"
                  ><i class="select-spinner"></i>Loading</span
                ></label
              ><select
                id="attendanceOption"
                v-model="filters.OptionId"
                class="form-control"
                :disabled="!filters.ClassId || listLoading.options"
                @change="onOptionChange"
              >
                <option value="">
                  {{ listLoading.options ? "Loading options…" : "All options" }}
                </option>
                <option
                  v-for="item in options"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.ClassOption }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="attendanceSection"
                >Section (Arm)<span
                  v-if="listLoading.sections"
                  class="select-loader"
                  ><i class="select-spinner"></i>Loading</span
                ></label
              ><select
                id="attendanceSection"
                v-model="filters.SectionId"
                class="form-control"
                :disabled="!filters.OptionId || listLoading.sections"
                @change="onSectionChange"
              >
                <option value="">
                  {{
                    listLoading.sections ? "Loading sections…" : "All sections"
                  }}
                </option>
                <option
                  v-for="item in sections"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.SectionName }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="attendanceStudent"
                >Student<span v-if="listLoading.students" class="select-loader"
                  ><i class="select-spinner"></i>Loading</span
                ></label
              ><select
                id="attendanceStudent"
                v-model="filters.StudentId"
                class="form-control"
                :disabled="!students.length || listLoading.students"
              >
                <option value="">
                  {{
                    listLoading.students ? "Loading students…" : "All students"
                  }}
                </option>
                <option
                  v-for="item in students"
                  :key="studentId(item)"
                  :value="String(studentId(item))"
                >
                  {{ studentName(item) }}
                </option>
              </select>
            </div>
            <div class="filter-actions">
              <button
                class="btn btn-primary"
                type="button"
                :disabled="loading || !filters.RegisteredTermId"
                @click="applyFilters"
              >
                <i class="fa fa-filter mr-1"></i>Apply filters</button
              ><button
                class="btn btn-outline-secondary"
                type="button"
                :disabled="loading"
                @click="clearFilters"
              >
                Clear filters
              </button>
            </div>
          </div>
          <p class="filter-hint">
            <i class="fa fa-circle-info mr-1"></i
            >{{
              mockMode
                ? "Demonstration mode supports term, class, option, section, and student filters. Change any filter and apply to preview the report."
                : "Live attendance reports require a section (Arm) or an individual student. The current school service returns section summaries; demonstration mode previews cross-class analytics."
            }}
          </p>
          <div v-if="activeFilterLabels.length" class="active-filters">
            <span>Applied:</span
            ><span
              v-for="label in activeFilterLabels"
              :key="label"
              class="filter-chip"
              >{{ label }}</span
            >
          </div>
        </div>
      </section>

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        <i class="fa fa-circle-exclamation mr-2"></i>{{ errorMessage }}
      </div>
      <div v-if="mockMode && hasGenerated" class="demo-banner">
        <i class="fa fa-flask"></i
        ><span
          ><strong>Demonstration data.</strong> These generated records are for
          preview only and are not school records.</span
        >
      </div>
      <div v-if="hasGenerated && !loading">
        <section class="kpi-grid mb-4" aria-label="Attendance summary">
          <article
            v-for="metric in metrics"
            :key="metric.label"
            class="kpi-card"
            :class="metric.tone"
          >
            <span class="kpi-icon"
              ><i class="fa" :class="metric.icon"></i
            ></span>
            <div>
              <small>{{ metric.label }}</small
              ><strong>{{ metric.value }}</strong
              ><span v-if="metric.note" class="kpi-note">{{
                metric.note
              }}</span>
            </div>
          </article>
        </section>

        <section class="charts-grid mb-4">
          <article class="chart-card card border-0 shadow-sm trend-card">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Attendance trend</h2>
                  <p>
                    {{
                      mockMode
                        ? "Daily records across the selected term"
                        : "Aggregate attendance totals by selected term"
                    }}
                  </p>
                </div>
                <div class="chart-legend">
                  <span><i class="legend-present"></i>Present or late</span
                  ><span><i class="legend-absent"></i>Absent</span>
                </div>
              </div>
              <div v-if="trendPoints.length" class="trend-chart">
                <svg
                  viewBox="0 0 720 230"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label="Present and absent attendance trend line chart"
                >
                  <g v-for="tick in trendTicks" :key="tick">
                    <line
                      x1="42"
                      :y1="trendY(tick)"
                      x2="710"
                      :y2="trendY(tick)"
                      class="gridline"
                    />
                    <text
                      x="34"
                      :y="trendY(tick) + 4"
                      text-anchor="end"
                      class="axis-label"
                    >
                      {{ tick }}%
                    </text>
                  </g>
                  <polyline
                    :points="trendLine"
                    fill="none"
                    stroke="#3378e8"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <polyline
                    :points="trendAbsentLine"
                    fill="none"
                    stroke="#e16868"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <g v-for="point in trendPoints" :key="point.label">
                    <circle
                      :cx="point.x"
                      :cy="trendY(point.rate)"
                      r="4"
                      fill="#3378e8"
                    >
                      <title>
                        {{ point.label }} · Present or late: {{ point.rate }}%
                      </title>
                    </circle>
                    <circle
                      :cx="point.x"
                      :cy="trendY(point.absentRate)"
                      r="3.5"
                      fill="#e16868"
                    >
                      <title>
                        {{ point.label }} · Absent: {{ point.absentRate }}%
                      </title>
                    </circle>
                    <text
                      :x="point.x"
                      y="220"
                      text-anchor="middle"
                      class="axis-label"
                    >
                      {{ point.label }}
                    </text>
                  </g>
                </svg>
              </div>
              <div v-else class="chart-empty">
                No trend data is available for these records.
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Attendance distribution</h2>
                  <p>Mutually exclusive outcomes across recorded days.</p>
                </div>
              </div>
              <div class="distribution-layout">
                <div
                  class="donut"
                  :style="donutStyle"
                  role="img"
                  :aria-label="`Attendance distribution: ${distributionTotal} sessions`"
                >
                  <div>
                    <strong>{{ distributionTotal }}</strong
                    ><small>records</small>
                  </div>
                </div>
                <ul class="distribution-legend">
                  <li v-for="item in distribution" :key="item.label">
                    <i :style="{ background: item.color }"></i
                    ><span
                      >{{ item.label
                      }}<small
                        >{{ item.count }} · {{ item.percent }}%</small
                      ></span
                    >
                  </li>
                </ul>
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Attendance by class</h2>
                  <p>Class-level attendance rates.</p>
                </div>
              </div>
              <div v-if="classRates.length" class="bar-list">
                <div
                  v-for="item in classRates"
                  :key="item.label"
                  class="bar-row"
                >
                  <span :title="item.label">{{ item.label }}</span>
                  <div class="bar-track">
                    <i :style="{ width: `${item.rate}%` }"></i>
                  </div>
                  <strong>{{ item.rate }}%</strong>
                </div>
              </div>
              <div v-else class="chart-empty">
                No class comparisons for the current selection.
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Attendance by section</h2>
                  <p>Rates within the selected class and option.</p>
                </div>
              </div>
              <div v-if="sectionRates.length" class="bar-list">
                <div
                  v-for="item in sectionRates"
                  :key="item.label"
                  class="bar-row"
                >
                  <span :title="item.label">{{ item.label }}</span>
                  <div class="bar-track">
                    <i
                      class="section-bar"
                      :style="{ width: `${item.rate}%` }"
                    ></i>
                  </div>
                  <strong>{{ item.rate }}%</strong>
                </div>
              </div>
              <div v-else class="chart-empty">
                Choose a class and option to compare sections.
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Students to follow up</h2>
                  <p>
                    Attendance below {{ threshold }}% (minimum
                    {{ minimumSample }} school days).
                  </p>
                </div>
              </div>
              <div v-if="atRiskStudents.length" class="risk-list">
                <button
                  v-for="row in atRiskStudents"
                  :key="row.id"
                  type="button"
                  class="risk-row"
                  @click="openStudent(row)"
                >
                  <span class="risk-avatar">{{ initials(row.name) }}</span
                  ><span
                    ><strong>{{ row.name }}</strong
                    ><small
                      >{{ row.className }} · {{ row.days }} days</small
                    ></span
                  ><b>{{ row.rate }}%</b>
                </button>
              </div>
              <div v-else class="chart-empty">
                No students below the attendance threshold.
              </div>
              <div v-if="bestStudent" class="best-student">
                <i class="fa fa-arrow-trend-up"></i
                ><span
                  >Highest attendance <strong>{{ bestStudent.name }}</strong> ·
                  {{ bestStudent.rate }}%</span
                >
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <div>
                  <h2>Weekly attendance pattern</h2>
                  <p>Attendance rate by day of week.</p>
                </div>
              </div>
              <div v-if="weeklyPattern.length" class="week-bars">
                <div
                  v-for="day in weeklyPattern"
                  :key="day.day"
                  class="week-column"
                >
                  <strong>{{ day.rate }}%</strong>
                  <div class="week-track">
                    <i :style="{ height: `${day.rate}%` }"></i>
                  </div>
                  <small>{{ day.day }}</small>
                </div>
              </div>
              <div v-else class="chart-empty">
                Daily records are not available for this selection.
              </div>
            </div>
          </article>
        </section>

        <section
          v-if="selectedStudent"
          class="student-detail card border-0 shadow-sm mb-4"
        >
          <div class="card-body">
            <div class="section-heading">
              <div>
                <h2>{{ selectedStudent.name }}</h2>
                <p>
                  {{ selectedStudent.admission }} ·
                  {{ selectedStudent.className }} ·
                  {{ selectedStudent.sectionName }}
                </p>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                @click="
                  filters.StudentId = '';
                  applyFilters();
                "
              >
                Close details
              </button>
            </div>
            <div class="detail-grid">
              <div>
                <small>Attendance rate</small
                ><strong>{{ selectedStudent.rate }}%</strong>
              </div>
              <div>
                <small>School days</small
                ><strong>{{ selectedStudent.days }}</strong>
              </div>
              <div>
                <small>Present</small
                ><strong>{{ selectedStudent.present }}</strong>
              </div>
              <div>
                <small>Absent</small
                ><strong>{{ selectedStudent.absent }}</strong>
              </div>
              <div>
                <small>Late</small><strong>{{ selectedStudent.late }}</strong>
              </div>
              <div>
                <small>Excused / unexcused</small
                ><strong
                  >{{ selectedStudent.excused }} /
                  {{ selectedStudent.unexcused }}</strong
                >
              </div>
            </div>
            <div v-if="selectedStudent.history.length" class="history-list">
              <h3>Recent attendance history</h3>
              <span
                v-for="entry in selectedStudent.history.slice(-12).reverse()"
                :key="entry.date"
                :class="`history-${entry.status.toLowerCase()}`"
                ><time>{{ entry.date }}</time
                ><b>{{ entry.status }}</b
                ><small v-if="entry.remark">{{ entry.remark }}</small></span
              >
            </div>
            <p v-else class="filter-hint mb-0">
              Daily history and remarks are not included in the live attendance
              summary contract.
            </p>
          </div>
        </section>

        <p class="print-context">
          Class Attendance Report · {{ activeFilterLabels.join(" · ") }}
        </p>
        <section class="table-card card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-heading">
              <div>
                <h2>Student attendance report</h2>
                <p>
                  {{ filteredRows.length }} students · status: Excellent ≥ 90%,
                  Good ≥ 75%, Needs improvement ≥ 60%, Critical &lt; 60%
                </p>
              </div>
              <div class="table-tools">
                <label class="search-wrap"
                  ><i class="fa fa-search"></i
                  ><input
                    v-model.trim="search"
                    type="search"
                    placeholder="Search name or admission no."
                    aria-label="Search students"
                    @input="page = 1" /></label
                ><button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="!filteredRows.length"
                  @click="exportCsv"
                >
                  <i class="fa fa-file-excel mr-1"></i>Export Excel</button
                ><button
                  class="btn btn-outline-secondary btn-sm"
                  type="button"
                  :disabled="!filteredRows.length"
                  @click="printReport"
                >
                  <i class="fa fa-print mr-1"></i>Print
                </button>
              </div>
            </div>
            <div v-if="filteredRows.length" class="table-responsive">
              <table class="table report-table mb-0">
                <thead>
                  <tr>
                    <th
                      v-for="column in columns"
                      :key="column.key"
                      :class="{ sortable: true }"
                      @click="sortBy(column.key)"
                    >
                      {{ column.label }}
                      <i class="fa ml-1" :class="sortIcon(column.key)"></i>
                    </th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in pagedRows"
                    :key="row.id"
                    :class="{ 'at-risk-row': row.rate < threshold }"
                  >
                    <td>{{ row.admission }}</td>
                    <td>
                      <strong>{{ row.name }}</strong>
                    </td>
                    <td>{{ row.className }}</td>
                    <td>{{ row.optionName }}</td>
                    <td>{{ row.sectionName }}</td>
                    <td>{{ row.days }}</td>
                    <td>{{ row.present + row.late }}</td>
                    <td>{{ row.present }}</td>
                    <td>{{ row.absent }}</td>
                    <td>{{ row.late }}</td>
                    <td>{{ row.excused }}</td>
                    <td>{{ row.unexcused }}</td>
                    <td>
                      <strong>{{ row.rate }}%</strong>
                    </td>
                    <td>
                      <span
                        class="status-badge"
                        :class="statusClass(row.rate)"
                        >{{ attendanceStatus(row.rate) }}</span
                      >
                    </td>
                    <td>
                      <button
                        class="btn btn-link btn-sm p-0"
                        type="button"
                        @click="openStudent(row)"
                      >
                        View details
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <span><i class="fa fa-folder-open"></i></span>
              <h3>No attendance records</h3>
              <p>
                {{
                  search
                    ? "Try another name or admission number."
                    : mockMode
                    ? "No students match these filters."
                    : "The attendance service returned no records. Try a different registered term or section."
                }}
              </p>
            </div>
            <div v-if="filteredRows.length" class="pagination-row">
              <span
                >Showing {{ pageStart + 1 }}–{{
                  Math.min(pageStart + pageSize, filteredRows.length)
                }}
                of {{ filteredRows.length }}</span
              >
              <div class="page-controls">
                <label
                  >Rows
                  <select
                    v-model.number="pageSize"
                    class="form-control form-control-sm"
                    @change="page = 1"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                  </select></label
                ><button
                  class="btn btn-light btn-sm"
                  :disabled="page === 1"
                  @click="page--"
                >
                  Previous</button
                ><span>{{ page }} / {{ pageCount }}</span
                ><button
                  class="btn btn-light btn-sm"
                  :disabled="page >= pageCount"
                  @click="page++"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>
        <p class="report-footnote">
          Attendance rate is present sessions ÷ total school days. In live mode,
          late, excused, and unexcused breakdowns appear only when supplied by
          the service. The current service provides section or student
          aggregates and does not provide daily history or cross-section
          comparisons.
        </p>
      </div>
      <div
        v-else-if="!loading && !errorMessage"
        class="welcome-state card border-0 shadow-sm"
      >
        <span><i class="fa fa-chart-line"></i></span>
        <h2>Build your attendance report</h2>
        <p>
          Select a registered term and apply filters to view attendance
          analytics.
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import PageSpinner from "@/components/PageSpinner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const SAMPLE_CLASSES = [
  {
    id: "c1",
    name: "Primary 4",
    options: [
      { id: "o1", name: "General", sections: ["Blue", "Gold"] },
      { id: "o2", name: "French", sections: ["A"] },
    ],
  },
  {
    id: "c2",
    name: "Primary 5",
    options: [{ id: "o3", name: "General", sections: ["Blue", "Gold"] }],
  },
  {
    id: "c3",
    name: "JSS 1",
    options: [{ id: "o4", name: "Science", sections: ["A", "B"] }],
  },
];

export default {
  name: "ClassAttendanceReport",
  components: { PageSpinner },
  data() {
    const mockTerms = [
      {
        registeredTermId: "demo-2025-1",
        schSession: "2025/2026",
        schTerm: "First Term",
      },
      {
        registeredTermId: "demo-2025-2",
        schSession: "2025/2026",
        schTerm: "Second Term",
      },
      {
        registeredTermId: "demo-2024-3",
        schSession: "2024/2025",
        schTerm: "Third Term",
      },
    ];
    const sampleRecords = this.createSampleRecords();
    const initialFilters = {
      RegisteredTermId: "",
      ClassId: "",
      OptionId: "",
      SectionId: "",
      StudentId: "",
    };
    return {
      loading: false,
      listLoading: {
        terms: false,
        classes: false,
        options: false,
        sections: false,
        students: false,
      },
      errorMessage: "",
      hasGenerated: false,
      mockMode: false,
      terms: [],
      classes: [],
      options: [],
      sections: [],
      students: [],
      records: [],
      rawAttendance: [],
      filters: initialFilters,
      appliedFilters: Object.assign({}, initialFilters),
      search: "",
      sortKey: "rate",
      sortDirection: "asc",
      page: 1,
      pageSize: 20,
      requestId: 0,
      threshold: 75,
      minimumSample: 5,
      mockTerms,
      sampleRecords,
    };
  },
  computed: {
    sourceRecords() {
      if (!this.appliedFilters) return [];
      const f = this.appliedFilters;
      return this.records.filter(
        (row) =>
          (!f.ClassId || row.classId === f.ClassId) &&
          (!f.OptionId || row.optionId === f.OptionId) &&
          (!f.SectionId || row.sectionId === f.SectionId) &&
          (!f.StudentId || row.id === f.StudentId)
      );
    },
    summary() {
      const rows = this.sourceRecords;
      const schoolDays = rows.reduce((sum, row) => sum + row.days, 0);
      const present = rows.reduce((sum, row) => sum + row.present, 0);
      const attended = rows.reduce(
        (sum, row) => sum + row.present + row.late,
        0
      );
      const absent = rows.reduce((sum, row) => sum + row.absent, 0);
      const late = rows.reduce((sum, row) => sum + row.late, 0);
      const excused = rows.reduce((sum, row) => sum + row.excused, 0);
      const unexcused = rows.reduce((sum, row) => sum + row.unexcused, 0);
      return {
        students: rows.length,
        schoolDays: rows.length ? Math.max(...rows.map((row) => row.days)) : 0,
        sessions: schoolDays,
        present,
        attended,
        absent,
        late,
        excused,
        unexcused,
        rate: schoolDays ? Math.round((attended * 100) / schoolDays) : 0,
        absenceRate: schoolDays ? Math.round((absent * 100) / schoolDays) : 0,
      };
    },
    metrics() {
      const m = this.summary;
      return [
        {
          label: "Total students",
          value: m.students,
          note: "Unique students",
          icon: "fa-users",
          tone: "tone-blue",
        },
        {
          label: "Total school days",
          value: m.schoolDays,
          note: "Maximum days in report",
          icon: "fa-calendar-days",
          tone: "tone-violet",
        },
        {
          label: "Attendance sessions",
          value: m.sessions,
          note: "Student-days expected",
          icon: "fa-list-check",
          tone: "tone-slate",
        },
        {
          label: "Students present",
          value: m.present,
          note: "Present student-days",
          icon: "fa-circle-check",
          tone: "tone-green",
        },
        {
          label: "Students absent",
          value: m.absent,
          note: "Absent student-days",
          icon: "fa-circle-xmark",
          tone: "tone-red",
        },
        {
          label: "Late arrivals",
          value: m.late,
          note: this.mockMode ? "Late student-days" : "Not separately reported",
          icon: "fa-clock",
          tone: "tone-amber",
        },
        {
          label: "Excused absences",
          value: m.excused,
          note: this.mockMode
            ? "Excused student-days"
            : "Not separately reported",
          icon: "fa-file-circle-check",
          tone: "tone-teal",
        },
        {
          label: "Unexcused absences",
          value: m.unexcused,
          note: this.mockMode
            ? "Unexcused student-days"
            : "Not separately reported",
          icon: "fa-triangle-exclamation",
          tone: "tone-rose",
        },
        {
          label: "Attendance rate",
          value: `${m.rate}%`,
          note: "Present ÷ expected days",
          icon: "fa-chart-line",
          tone: "tone-green",
        },
        {
          label: "Absence rate",
          value: `${m.absenceRate}%`,
          note: "Absent ÷ expected days",
          icon: "fa-chart-column",
          tone: "tone-amber",
        },
      ];
    },
    filteredRows() {
      const needle = this.search.toLowerCase();
      return this.sourceRecords
        .filter(
          (row) =>
            !needle ||
            `${row.name} ${row.admission}`.toLowerCase().includes(needle)
        )
        .slice()
        .sort((a, b) => {
          const av = a[this.sortKey];
          const bv = b[this.sortKey];
          const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
          return this.sortDirection === "asc" ? cmp : -cmp;
        });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pageStart() {
      return (this.page - 1) * this.pageSize;
    },
    pagedRows() {
      return this.filteredRows.slice(
        this.pageStart,
        this.pageStart + this.pageSize
      );
    },
    columns() {
      return [
        { key: "admission", label: "Admission no." },
        { key: "name", label: "Student" },
        { key: "className", label: "Class" },
        { key: "optionName", label: "Option" },
        { key: "sectionName", label: "Section" },
        { key: "days", label: "School days" },
        { key: "present", label: "Sessions attended" },
        { key: "present", label: "Present" },
        { key: "absent", label: "Absent" },
        { key: "late", label: "Late" },
        { key: "excused", label: "Excused" },
        { key: "unexcused", label: "Unexcused" },
        { key: "rate", label: "Attendance" },
        { key: "rate", label: "Status" },
      ];
    },
    distribution() {
      const s = this.summary;
      const total = s.sessions;
      return [
        { label: "Present", count: s.present, color: "#278a64" },
        { label: "Absent", count: s.unexcused, color: "#e16868" },
        { label: "Late", count: s.late, color: "#e5a52f" },
        { label: "Excused", count: s.excused, color: "#568bdd" },
      ]
        .map((item) =>
          Object.assign({}, item, {
            percent: total ? ((item.count / total) * 100).toFixed(1) : "0.0",
          })
        )
        .filter((item) => item.count > 0);
    },
    distributionTotal() {
      return this.distribution.reduce((sum, item) => sum + item.count, 0);
    },
    donutStyle() {
      const colors = this.distribution;
      let cursor = 0;
      const stops = colors.map((item) => {
        const start = cursor;
        cursor += Number(item.percent);
        return `${item.color} ${start}% ${cursor}%`;
      });
      return {
        background: stops.length
          ? `conic-gradient(${stops.join(",")})`
          : "#e8edf4",
      };
    },
    trendPoints() {
      if (this.mockMode) {
        const daily = {};
        this.sourceRecords.forEach((row) =>
          row.history.forEach((entry) => {
            if (!daily[entry.date])
              daily[entry.date] = { expected: 0, present: 0 };
            daily[entry.date].expected += 1;
            if (entry.status === "Present" || entry.status === "Late")
              daily[entry.date].present += 1;
          })
        );
        const entries = Object.keys(daily).sort().slice(-15);
        return entries.map((date, index) => ({
          label: date.slice(5),
          rate: daily[date].expected
            ? Math.round((daily[date].present * 100) / daily[date].expected)
            : 0,
          absentRate: daily[date].expected
            ? Math.round(
                ((daily[date].expected - daily[date].present) * 100) /
                  daily[date].expected
              )
            : 0,
          x:
            entries.length < 2
              ? 360
              : 46 + (index * 660) / (entries.length - 1),
        }));
      }
      return this.sourceRecords.length
        ? [
            {
              label: "Term",
              rate: this.summary.rate,
              absentRate: this.summary.absenceRate,
              x: 360,
            },
          ]
        : [];
    },
    trendTicks() {
      return [0, 25, 50, 75, 100];
    },
    trendLine() {
      return this.trendPoints
        .map((point) => `${point.x},${this.trendY(point.rate)}`)
        .join(" ");
    },
    trendAbsentLine() {
      return this.trendPoints
        .map((point) => `${point.x},${this.trendY(point.absentRate)}`)
        .join(" ");
    },
    classRates() {
      return this.groupRates("classId", "className");
    },
    sectionRates() {
      return this.groupRates("sectionId", "sectionName");
    },
    atRiskStudents() {
      return this.sourceRecords
        .filter(
          (row) => row.days >= this.minimumSample && row.rate < this.threshold
        )
        .sort((a, b) => a.rate - b.rate)
        .slice(0, 5);
    },
    bestStudent() {
      return (
        this.sourceRecords
          .filter((row) => row.days >= this.minimumSample)
          .slice()
          .sort((a, b) => b.rate - a.rate)[0] || null
      );
    },
    weeklyPattern() {
      if (!this.mockMode) return [];
      const values = DAYS.map((day) => ({ day, expected: 0, present: 0 }));
      this.sourceRecords.forEach((row) =>
        row.history.forEach((item) => {
          const index = new Date(`${item.date}T12:00:00`).getDay();
          const day = values.find(
            (v) =>
              v.day === ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][index]
          );
          if (day) {
            day.expected += 1;
            if (item.status === "Present" || item.status === "Late")
              day.present += 1;
          }
        })
      );
      return values.map((item) => ({
        day: item.day,
        rate: item.expected
          ? Math.round((item.present * 100) / item.expected)
          : 0,
      }));
    },
    selectedStudent() {
      return (
        this.sourceRecords.find((row) => row.id === this.filters.StudentId) ||
        null
      );
    },
    activeFilterLabels() {
      if (!this.appliedFilters) return [];
      const f = this.appliedFilters;
      const labels = [];
      const term = this.terms.find(
        (item) => String(item.registeredTermId) === f.RegisteredTermId
      );
      if (term) labels.push(`${term.schSession} · ${term.schTerm}`);
      const cls = this.classes.find((item) => String(item.Id) === f.ClassId);
      if (cls) labels.push(cls.Sch_Class);
      const option = this.options.find(
        (item) => String(item.Id) === f.OptionId
      );
      if (option) labels.push(option.ClassOption);
      const section = this.sections.find(
        (item) => String(item.Id) === f.SectionId
      );
      if (section) labels.push(section.SectionName);
      const student = this.students.find(
        (item) => String(this.studentId(item)) === f.StudentId
      );
      if (student) labels.push(this.studentName(student));
      return labels;
    },
  },
  watch: {
    pageCount(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    getArray(value) {
      if (Array.isArray(value)) return value;
      if (value && Array.isArray(value.data)) return value.data;
      return [];
    },
    studentId(item) {
      return item.studentId || item.StudentId || item.Id || item.id;
    },
    studentName(item) {
      return (
        item.studentName ||
        item.StudentName ||
        item.fullName ||
        item.FullName ||
        [item.firstName, item.middleName, item.lastName]
          .filter(Boolean)
          .join(" ") ||
        "Student"
      );
    },
    createSampleRecords() {
      const records = [];
      let studentNumber = 0;
      SAMPLE_CLASSES.forEach((cls, classIndex) =>
        cls.options.forEach((option) =>
          option.sections.forEach((section) => {
            for (let studentIndex = 0; studentIndex < 5; studentIndex += 1) {
              studentNumber += 1;
              const id = `demo-student-${studentNumber}`;
              const dates = [];
              const start = new Date("2025-09-08T12:00:00");
              const cursor = new Date(start);
              while (dates.length < 40) {
                if (cursor.getDay() !== 0 && cursor.getDay() !== 6)
                  dates.push(cursor.toISOString().slice(0, 10));
                cursor.setDate(cursor.getDate() + 1);
              }
              const profile =
                studentIndex === 0
                  ? 0
                  : studentIndex === 1
                  ? 1
                  : studentIndex === 2
                  ? 2
                  : studentIndex === 3
                  ? 3
                  : 4;
              const absenceCount = [1, 7, 13, 4, 9][profile] + (classIndex % 2);
              const lateCount = [1, 2, 1, 6, 3][profile];
              const history = dates.map((date, index) => {
                let status = "Present";
                let remark = "";
                if (index < absenceCount) {
                  status = index % 3 === 0 ? "Excused" : "Absent";
                  remark =
                    status === "Excused"
                      ? "Family appointment"
                      : "No reason recorded";
                } else if (index < absenceCount + lateCount) {
                  status = "Late";
                  remark = "Arrived after registration";
                }
                return { date, status, remark };
              });
              const counts = history.reduce((acc, entry) => {
                acc[entry.status.toLowerCase()] =
                  (acc[entry.status.toLowerCase()] || 0) + 1;
                return acc;
              }, {});
              const days = history.length;
              const present =
                days -
                (counts.absent || 0) -
                (counts.excused || 0) -
                (counts.late || 0);
              const currentClass = SAMPLE_CLASSES[classIndex];
              const common = {
                id,
                classId: cls.id,
                optionId: option.id,
                sectionId: `${option.id}-${section}`,
                name: [
                  "Amina Okafor",
                  "Daniel Bello",
                  "Zara Ibrahim",
                  "Tunde Adeyemi",
                  "Chika Nwosu",
                ][(studentNumber - 1) % 5],
                admission: `SD-${String(202500 + studentNumber).padStart(
                  6,
                  "0"
                )}`,
                className: currentClass.name,
                optionName: option.name,
                sectionName: section,
                days,
                present,
                absent: counts.absent || 0,
                late: counts.late || 0,
                excused: counts.excused || 0,
                unexcused: counts.absent || 0,
                rate: Math.round(((present + (counts.late || 0)) * 100) / days),
                history,
              };
              records.push(
                Object.assign({}, common, {
                  termId: "demo-2025-1",
                  absent: (counts.absent || 0) + (counts.excused || 0),
                })
              );
              const secondAbsent = Math.ceil(absenceCount * 0.75);
              const secondExcused = Math.ceil(absenceCount * 0.25);
              const secondLate = Math.ceil(lateCount * 0.75);
              records.push(
                Object.assign({}, common, {
                  termId: "demo-2025-2",
                  days: 30,
                  history: history.slice(0, 30),
                  present: Math.max(
                    0,
                    30 - secondAbsent - secondLate - secondExcused
                  ),
                  absent: secondAbsent + secondExcused,
                  late: secondLate,
                  excused: secondExcused,
                  unexcused: secondAbsent,
                  rate: Math.round(
                    ((30 - secondAbsent - secondExcused) * 100) / 30
                  ),
                })
              );
              records.push(
                Object.assign({}, common, {
                  termId: "demo-2024-3",
                  absent: (counts.absent || 0) + (counts.excused || 0),
                  rate: Math.round(
                    ((present + (counts.late || 0)) * 100) / days
                  ),
                })
              );
            }
          })
        )
      );
      return records;
    },
    trendY(rate) {
      return 202 - Number(rate) * 1.78;
    },
    groupRates(idKey, labelKey) {
      const groups = {};
      this.sourceRecords.forEach((row) => {
        const key = row[idKey];
        if (!groups[key])
          groups[key] = { label: row[labelKey], present: 0, days: 0 };
        groups[key].present += row.present + row.late;
        groups[key].days += row.days;
      });
      return Object.keys(groups)
        .map((key) => ({
          label: groups[key].label,
          rate: groups[key].days
            ? Math.round((groups[key].present * 100) / groups[key].days)
            : 0,
        }))
        .sort((a, b) => b.rate - a.rate)
        .slice(0, 8);
    },
    async loadFilterOptions() {
      this.listLoading.terms = true;
      this.listLoading.classes = true;
      try {
        const [termResponse, classResponse] = await Promise.all([
          this.$store.dispatch(
            "PortalSchoolSessionTerm/GetPortalRegisteredTerm",
            { SearchValue: null, PageNumber: 1, PageSize: 100 }
          ),
          this.$store.dispatch("getclassinclassassigned"),
        ]);
        this.terms = this.getArray(termResponse);
        this.classes = this.getArray(classResponse);
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load the registered terms and assigned classes."
        );
      } finally {
        this.listLoading.terms = false;
        this.listLoading.classes = false;
      }
    },
    async onClassChange() {
      this.filters.OptionId = "";
      this.filters.SectionId = "";
      this.filters.StudentId = "";
      this.options = [];
      this.sections = [];
      this.students = [];
      if (!this.filters.ClassId) return;
      if (this.mockMode) {
        const source = SAMPLE_CLASSES.find(
          (item) => item.id === this.filters.ClassId
        );
        this.options = source
          ? source.options.map((item) => ({
              Id: item.id,
              ClassOption: item.name,
            }))
          : [];
        this.students = this.sampleRecords
          .filter(
            (row) =>
              row.termId === this.filters.RegisteredTermId &&
              row.classId === this.filters.ClassId
          )
          .map((row) => ({
            Id: row.id,
            StudentName: row.name,
            admNo: row.admission,
          }));
        return;
      }
      this.listLoading.options = true;
      this.listLoading.students = true;
      try {
        const responses = await Promise.all([
          this.$store.dispatch(
            "getoptioninclassassigned",
            this.filters.ClassId
          ),
          this.$store.dispatch("getByClassIdStudent", this.filters.ClassId),
        ]);
        this.options = this.getArray(responses[0]);
        this.students = this.getArray(responses[1]);
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load options for this class."
        );
      } finally {
        this.listLoading.options = false;
        this.listLoading.students = false;
      }
    },
    async onOptionChange() {
      this.filters.SectionId = "";
      this.filters.StudentId = "";
      this.sections = [];
      this.students = [];
      if (!this.filters.OptionId) return;
      if (this.mockMode) {
        const source = SAMPLE_CLASSES.reduce(
          (all, cls) => all.concat(cls.options),
          []
        ).find((item) => item.id === this.filters.OptionId);
        this.sections = source
          ? source.sections.map((name) => ({
              Id: `${source.id}-${name}`,
              SectionName: name,
            }))
          : [];
        this.students = this.sampleRecords
          .filter(
            (row) =>
              row.termId === this.filters.RegisteredTermId &&
              row.optionId === this.filters.OptionId
          )
          .map((row) => ({
            Id: row.id,
            StudentName: row.name,
            admNo: row.admission,
          }));
        return;
      }
      this.listLoading.sections = true;
      this.listLoading.students = true;
      try {
        const responses = await Promise.all([
          this.$store.dispatch("getarminclassassigned", this.filters.OptionId),
          this.$store.dispatch("getstudentbyoption", {
            OptionId: this.filters.OptionId,
            ClassId: this.filters.ClassId,
          }),
        ]);
        this.sections = this.getArray(responses[0]);
        this.students = this.getArray(responses[1]);
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load sections for this option."
        );
      } finally {
        this.listLoading.sections = false;
        this.listLoading.students = false;
      }
    },
    async onSectionChange() {
      this.filters.StudentId = "";
      this.students = [];
      if (!this.filters.SectionId) return;
      if (this.mockMode) {
        this.students = this.sampleRecords
          .filter(
            (row) =>
              row.termId === this.filters.RegisteredTermId &&
              row.sectionId === this.filters.SectionId
          )
          .map((row) => ({
            Id: row.id,
            StudentName: row.name,
            admNo: row.admission,
          }));
        return;
      }
      this.listLoading.students = true;
      try {
        this.students = this.getArray(
          await this.$store.dispatch("getstudentbyarm", {
            ArmId: this.filters.SectionId,
            ClassId: this.filters.ClassId,
          })
        );
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load students for this section."
        );
      } finally {
        this.listLoading.students = false;
      }
    },
    async applyFilters() {
      this.errorMessage = "";
      if (!this.filters.RegisteredTermId) {
        this.errorMessage = "Select a registered term to build the report.";
        return;
      }
      this.appliedFilters = Object.assign({}, this.filters);
      this.search = "";
      this.page = 1;
      if (this.mockMode) {
        this.records = this.sampleRecords.filter(
          (row) => row.termId === this.appliedFilters.RegisteredTermId
        );
        this.hasGenerated = true;
        return;
      }
      if (!this.filters.SectionId && !this.filters.StudentId) {
        this.hasGenerated = false;
        this.errorMessage =
          "The live attendance service requires a section (Arm) or a student. Choose a section or student, or switch to demonstration mode for term and class-wide analytics.";
        return;
      }
      const request = ++this.requestId;
      this.loading = true;
      this.hasGenerated = false;
      try {
        const payload = this.filters.StudentId
          ? await this.$store.dispatch(
              "PortalClassAttendance/GetClassAttendancesByStudentId",
              {
                StudentId: this.filters.StudentId,
                RegisteredTermId: this.filters.RegisteredTermId,
              }
            )
          : await this.$store.dispatch(
              "PortalClassAttendance/GetClassAttendances",
              {
                SectionId: this.filters.SectionId,
                RegisteredTermId: this.filters.RegisteredTermId,
              }
            );
        if (request !== this.requestId) return;
        const data =
          payload && payload.responseCode === "00"
            ? payload.data
            : payload && payload.data
            ? payload.data
            : payload;
        this.rawAttendance = this.getArray(data);
        this.records = this.rawAttendance
          .map((item) => this.normalizeAttendance(item))
          .filter(Boolean);
        this.hasGenerated = true;
        if (payload && payload.responseCode && payload.responseCode !== "00")
          this.errorMessage =
            payload.responseMessage ||
            "The attendance service could not return this report.";
      } catch (error) {
        if (request === this.requestId)
          this.errorMessage = this.errorText(
            error,
            "Could not load attendance records."
          );
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    normalizeAttendance(item) {
      const days = Number(
        item.totalSchoolOpened !== undefined
          ? item.totalSchoolOpened
          : item.TotalSchoolOpened || 0
      );
      const present = Number(
        item.timePresent !== undefined
          ? item.timePresent
          : item.TimePresent || 0
      );
      const absent = Math.max(days - present, 0);
      const student =
        this.students.find(
          (person) =>
            String(this.studentId(person)) ===
            String(item.studentId || item.StudentId || item.Id)
        ) || {};
      const classItem =
        this.classes.find(
          (row) => String(row.Id) === String(this.filters.ClassId)
        ) || {};
      const optionItem =
        this.options.find(
          (row) => String(row.Id) === String(this.filters.OptionId)
        ) || {};
      const sectionItem =
        this.sections.find(
          (row) => String(row.Id) === String(this.filters.SectionId)
        ) || {};
      return {
        id: String(
          item.studentId ||
            item.StudentId ||
            item.Id ||
            item.admNo ||
            item.AdmNo ||
            this.studentId(student) ||
            this.studentName(item)
        ),
        admission:
          item.admNo ||
          item.AdmNo ||
          item.admissionNumber ||
          item.AdmissionNumber ||
          "—",
        name: item.studentName || item.StudentName || this.studentName(student),
        classId: this.filters.ClassId,
        optionId: this.filters.OptionId,
        sectionId: this.filters.SectionId,
        className:
          item.sch_Class || item.Sch_Class || classItem.Sch_Class || "—",
        optionName:
          item.classOption || item.ClassOption || optionItem.ClassOption || "—",
        sectionName:
          item.sectionName ||
          item.SectionName ||
          sectionItem.SectionName ||
          "—",
        days,
        present,
        absent,
        late: Number(item.lateCount || item.LateCount || 0),
        excused: Number(
          item.excusedAbsenceCount || item.ExcusedAbsenceCount || 0
        ),
        unexcused: Number(
          item.unexcusedAbsenceCount || item.UnexcusedAbsenceCount || absent
        ),
        rate: days
          ? Math.round(
              ((present + Number(item.lateCount || item.LateCount || 0)) *
                100) /
                days
            )
          : 0,
        history: [],
      };
    },
    toggleMode() {
      this.mockMode = !this.mockMode;
      this.errorMessage = "";
      this.hasGenerated = false;
      this.appliedFilters = null;
      this.records = [];
      if (this.mockMode) {
        this.terms = this.mockTerms;
        this.classes = SAMPLE_CLASSES.map((item) => ({
          Id: item.id,
          Sch_Class: item.name,
        }));
        this.filters = {
          RegisteredTermId: "demo-2025-1",
          ClassId: "",
          OptionId: "",
          SectionId: "",
          StudentId: "",
        };
        this.appliedFilters = Object.assign({}, this.filters);
        this.records = this.sampleRecords.filter(
          (row) => row.termId === this.filters.RegisteredTermId
        );
        this.hasGenerated = true;
        this.options = [];
        this.sections = [];
        this.students = [];
      } else {
        this.filters = {
          RegisteredTermId: "",
          ClassId: "",
          OptionId: "",
          SectionId: "",
          StudentId: "",
        };
        this.options = [];
        this.sections = [];
        this.students = [];
        this.loadFilterOptions();
      }
    },
    clearFilters() {
      this.errorMessage = "";
      this.hasGenerated = false;
      this.appliedFilters = null;
      this.records = [];
      this.search = "";
      this.page = 1;
      this.filters = {
        RegisteredTermId: this.mockMode ? "demo-2025-1" : "",
        ClassId: "",
        OptionId: "",
        SectionId: "",
        StudentId: "",
      };
      this.options = [];
      this.sections = [];
      this.students = [];
    },
    errorText(error, fallback) {
      return error && error.response && error.response.data
        ? String(error.response.data)
        : error && error.message
        ? error.message
        : fallback;
    },
    sortBy(key) {
      if (this.sortKey === key)
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      else {
        this.sortKey = key;
        this.sortDirection = key === "rate" || key === "absent" ? "asc" : "asc";
      }
      this.page = 1;
    },
    sortIcon(key) {
      return this.sortKey !== key
        ? "fa-sort text-muted"
        : this.sortDirection === "asc"
        ? "fa-sort-up"
        : "fa-sort-down";
    },
    attendanceStatus(rate) {
      return rate >= 90
        ? "Excellent"
        : rate >= 75
        ? "Good"
        : rate >= 60
        ? "Needs improvement"
        : "Critical";
    },
    statusClass(rate) {
      return rate >= 90
        ? "status-excellent"
        : rate >= 75
        ? "status-good"
        : rate >= 60
        ? "status-watch"
        : "status-critical";
    },
    initials(name) {
      return String(name)
        .split(" ")
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    openStudent(row) {
      this.filters.StudentId = row.id;
      this.appliedFilters = Object.assign({}, this.appliedFilters, {
        StudentId: row.id,
      });
      this.records = [row];
      this.hasGenerated = true;
      this.page = 1;
    },
    exportCsv() {
      const rows = this.filteredRows.map((row) => [
        row.admission,
        row.name,
        row.className,
        row.optionName,
        row.sectionName,
        row.days,
        row.present + row.late,
        row.present,
        row.absent,
        row.late,
        row.excused,
        row.unexcused,
        `${row.rate}%`,
        this.attendanceStatus(row.rate),
      ]);
      const term = this.terms.find(
        (item) =>
          String(item.registeredTermId) ===
          (this.appliedFilters && this.appliedFilters.RegisteredTermId)
      );
      const sheetRows = [
        ["Class Attendance Report"],
        [
          "Registered term",
          term ? `${term.schSession} · ${term.schTerm}` : "—",
        ],
        [
          "Applied filters",
          this.activeFilterLabels.join(", ") || "All available records",
        ],
        [],
        [
          "Admission no.",
          "Student name",
          "Class",
          "Option",
          "Section",
          "Total school days",
          "Sessions attended",
          "Present",
          "Absent",
          "Late",
          "Excused absences",
          "Unexcused absences",
          "Attendance percentage",
          "Status",
        ],
      ].concat(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(sheetRows),
        "Attendance"
      );
      XLSX.writeFile(workbook, "class-attendance-report.xlsx");
    },
    printReport() {
      window.print();
    },
    mountedLoad() {
      this.loadFilterOptions();
    },
  },
  mounted() {
    if (!this.mockMode) this.mountedLoad();
  },
};
</script>

<style scoped>
.print-context {
  display: none;
}
.select-loader {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: 0.45rem;
  color: #6c7c90;
  font-size: 0.66rem;
  font-weight: 500;
  vertical-align: middle;
}
.select-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid #d9e2ed;
  border-top-color: #4385db;
  border-radius: 50%;
  animation: select-spin 0.7s linear infinite;
}
@keyframes select-spin {
  to {
    transform: rotate(360deg);
  }
}
.report-shell {
  padding-bottom: 3rem;
}
.report-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}
.report-heading h1 {
  font-size: 1.85rem;
  font-weight: 750;
  color: #17283f;
  margin: 0.25rem 0;
}
.report-heading p,
.section-heading p,
.table-heading p {
  margin: 0;
  color: #77859a;
}
.eyebrow {
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #5284c5;
}
.mode-tools {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.mode-pill {
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
}
.mode-pill i {
  margin-right: 0.4rem;
}
.live-pill {
  background: #e9f6ef;
  color: #26754e;
}
.demo-pill {
  background: #fff5df;
  color: #946415;
}
.filter-panel,
.chart-card,
.table-card,
.student-detail,
.welcome-state {
  border-radius: 14px;
}
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.15rem;
}
.section-heading h2,
.table-heading h2 {
  font-size: 1.02rem;
  color: #20334d;
  font-weight: 750;
  margin: 0 0 0.2rem;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(145px, 1fr));
  gap: 0.75rem 1rem;
}
.filter-grid .form-group {
  margin-bottom: 0;
}
.filter-grid label {
  font-size: 0.77rem;
  font-weight: 700;
  color: #41536a;
  margin-bottom: 0.4rem;
}
.filter-grid .form-control {
  font-size: 0.85rem;
}
.required {
  color: #d25757;
}
.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.55rem;
  padding-top: 1.6rem;
}
.filter-hint {
  font-size: 0.78rem;
  color: #78879a;
  margin: 0.9rem 0 0;
}
.active-filters {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.85rem;
  color: #687990;
  font-size: 0.76rem;
}
.filter-chip {
  background: #f0f4f9;
  border-radius: 999px;
  padding: 0.25rem 0.6rem;
  color: #435772;
}
.demo-banner {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: #fff6e5;
  border: 1px solid #f3dfb2;
  color: #77571d;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.84rem;
}
.demo-banner i {
  font-size: 1.1rem;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.85rem;
}
.kpi-card {
  min-height: 108px;
  background: white;
  border-radius: 13px;
  box-shadow: 0 4px 16px rgba(27, 50, 78, 0.07);
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.kpi-icon {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  flex: 0 0 auto;
}
.kpi-card small {
  display: block;
  color: #738197;
  font-size: 0.72rem;
  font-weight: 650;
}
.kpi-card strong {
  display: block;
  font-size: 1.3rem;
  line-height: 1.4;
  color: #22354e;
}
.kpi-note {
  display: block;
  color: #8a96a6;
  font-size: 0.66rem;
  margin-top: 0.1rem;
}
.tone-blue .kpi-icon {
  background: #eaf2ff;
  color: #427cdb;
}
.tone-violet .kpi-icon {
  background: #f0ecff;
  color: #795bd1;
}
.tone-slate .kpi-icon {
  background: #edf1f5;
  color: #61738b;
}
.tone-green .kpi-icon {
  background: #e7f6ee;
  color: #30865d;
}
.tone-red .kpi-icon,
.tone-rose .kpi-icon {
  background: #fff0ef;
  color: #cb6260;
}
.tone-amber .kpi-icon {
  background: #fff5e3;
  color: #bd8320;
}
.tone-teal .kpi-icon {
  background: #e6f7f5;
  color: #25877d;
}
.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.chart-card .card-body {
  padding: 1.25rem;
}
.trend-card {
  grid-column: span 2;
}
.chart-legend {
  display: flex;
  gap: 0.8rem;
  color: #75849a;
  font-size: 0.72rem;
}
.chart-legend i,
.distribution-legend i {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 0.35rem;
}
.legend-present {
  background: #3378e8;
}
.legend-absent {
  background: #e16868;
}
.trend-chart {
  height: 210px;
}
.trend-chart svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.gridline {
  stroke: #edf1f6;
  stroke-width: 1;
}
.axis-label {
  fill: #8492a6;
  font-size: 10px;
}
.distribution-layout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  min-height: 190px;
}
.donut {
  width: 158px;
  height: 158px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  position: relative;
  flex: none;
}
.donut:before {
  content: "";
  position: absolute;
  width: 96px;
  height: 96px;
  background: white;
  border-radius: 50%;
}
.donut div {
  position: relative;
  text-align: center;
}
.donut strong,
.donut small {
  display: block;
}
.donut strong {
  font-size: 1.25rem;
  color: #263b56;
}
.donut small {
  font-size: 0.68rem;
  color: #8794a6;
}
.distribution-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.65rem;
}
.distribution-legend li {
  display: flex;
  align-items: flex-start;
  color: #53637a;
  font-size: 0.76rem;
}
.distribution-legend small {
  display: block;
  color: #8995a5;
  font-size: 0.68rem;
}
.bar-list {
  display: grid;
  gap: 1rem;
  padding: 0.6rem 0;
}
.bar-row {
  display: grid;
  grid-template-columns: minmax(80px, 1fr) 2fr 42px;
  align-items: center;
  gap: 0.7rem;
  font-size: 0.75rem;
  color: #56677e;
}
.bar-row > span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-row strong {
  text-align: right;
  color: #334760;
}
.bar-track {
  height: 9px;
  border-radius: 999px;
  background: #edf1f6;
  overflow: hidden;
}
.bar-track i {
  height: 100%;
  display: block;
  background: #4b89e9;
  border-radius: 999px;
}
.bar-track i.section-bar {
  background: #43aa85;
}
.chart-empty {
  min-height: 135px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #929eae;
  font-size: 0.83rem;
}
.risk-list {
  display: grid;
}
.risk-row {
  display: flex;
  align-items: center;
  text-align: left;
  gap: 0.65rem;
  padding: 0.6rem 0;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #eff2f6;
}
.risk-row > span:nth-child(2) {
  flex: 1;
  min-width: 0;
}
.risk-row strong,
.risk-row small {
  display: block;
}
.risk-row strong {
  font-size: 0.8rem;
  color: #31445e;
}
.risk-row small {
  font-size: 0.68rem;
  color: #8a96a6;
}
.risk-row b {
  color: #bd5858;
  font-size: 0.83rem;
}
.risk-avatar {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: #fff0ee;
  border-radius: 50%;
  color: #bd6260;
  font-size: 0.66rem;
  font-weight: 800;
}
.best-student {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.8rem;
  color: #758399;
  font-size: 0.72rem;
}
.best-student i {
  color: #30865d;
}
.best-student strong {
  color: #34516d;
}
.week-bars {
  height: 160px;
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  padding-top: 0.3rem;
}
.week-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
  flex: 1;
}
.week-column strong {
  font-size: 0.67rem;
  color: #708198;
}
.week-track {
  height: 95px;
  width: min(28px, 70%);
  display: flex;
  align-items: flex-end;
  background: #eef2f6;
  border-radius: 8px;
  overflow: hidden;
}
.week-track i {
  display: block;
  width: 100%;
  background: #4b89e9;
  border-radius: 8px;
}
.week-column small {
  font-size: 0.67rem;
  color: #8190a3;
}
.table-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.9rem;
}
.table-tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.table-heading p {
  font-size: 0.75rem;
}
.search-wrap {
  height: 34px;
  border: 1px solid #dce3ec;
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding: 0 0.6rem;
  gap: 0.45rem;
  color: #8794a6;
  margin: 0;
}
.search-wrap input {
  width: 190px;
  border: 0;
  outline: 0;
  font-size: 0.76rem;
}
.report-table th {
  font-size: 0.68rem;
  color: #738198;
  text-transform: uppercase;
  letter-spacing: 0.035em;
  white-space: nowrap;
  background: #f8fafc;
  padding: 0.8rem 0.65rem;
}
.report-table td {
  font-size: 0.77rem;
  color: #43556c;
  vertical-align: middle;
  padding: 0.7rem 0.65rem;
  white-space: nowrap;
}
.report-table th.sortable {
  cursor: pointer;
}
.at-risk-row {
  background: #fffafa;
}
.status-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 0.25rem 0.5rem;
  font-size: 0.66rem;
  font-weight: 750;
}
.status-excellent {
  color: #23714c;
  background: #e7f5ec;
}
.status-good {
  color: #3c6fa9;
  background: #e9f2fd;
}
.status-watch {
  color: #9a6a18;
  background: #fff4db;
}
.status-critical {
  color: #aa4b4b;
  background: #ffebea;
}
.empty-state,
.welcome-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #8290a2;
}
.empty-state > span,
.welcome-state > span {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #f0f4f8;
  color: #8092a8;
  margin: 0 auto 0.8rem;
}
.empty-state h3,
.welcome-state h2 {
  font-size: 1.05rem;
  color: #354b65;
}
.empty-state p,
.welcome-state p {
  font-size: 0.82rem;
  margin: 0;
}
.pagination-row {
  border-top: 1px solid #edf0f5;
  padding: 0.8rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  color: #7e8b9d;
  font-size: 0.75rem;
}
.page-controls {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.page-controls label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
}
.page-controls select {
  width: auto;
}
.report-footnote {
  font-size: 0.72rem;
  color: #8793a3;
  margin: 0.8rem 0.2rem;
}
.student-detail {
  margin-top: 1rem;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.8rem;
  background: #f7f9fc;
  border-radius: 9px;
  padding: 1rem;
}
.detail-grid small,
.detail-grid strong {
  display: block;
}
.detail-grid small {
  font-size: 0.7rem;
  color: #7e8b9d;
}
.detail-grid strong {
  color: #304761;
  font-size: 0.95rem;
}
.history-list {
  margin-top: 1rem;
}
.history-list h3 {
  font-size: 0.86rem;
  color: #354b65;
}
.history-list span {
  display: grid;
  grid-template-columns: 110px 90px 1fr;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid #eef1f5;
  font-size: 0.76rem;
  color: #617188;
}
.history-present b {
  color: #30865d;
}
.history-absent b,
.history-unexcused b {
  color: #c45656;
}
.history-late b {
  color: #b78320;
}
.history-excused b {
  color: #4e80c2;
}
@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .filter-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }
  .filter-actions {
    padding-top: 1.45rem;
  }
}
@media (max-width: 767px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
  .trend-card {
    grid-column: auto;
  }
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .detail-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .table-heading {
    align-items: flex-start;
  }
  .table-tools {
    width: 100%;
  }
  .search-wrap {
    flex: 1;
  }
  .search-wrap input {
    width: 100%;
    min-width: 100px;
  }
}
@media (max-width: 480px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    padding-top: 0.2rem;
  }
  .filter-actions .btn {
    flex: 1;
  }
  .kpi-card {
    padding: 0.75rem;
    min-height: 96px;
  }
  .kpi-card strong {
    font-size: 1.08rem;
  }
  .kpi-icon {
    width: 32px;
    height: 32px;
  }
  .distribution-layout {
    gap: 0.8rem;
  }
  .donut {
    width: 125px;
    height: 125px;
  }
  .donut:before {
    width: 76px;
    height: 76px;
  }
  .detail-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .pagination-row {
    align-items: flex-start;
    flex-direction: column;
  }
  .mode-tools {
    width: 100%;
    justify-content: space-between;
  }
}
@media print {
  .print-context {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: #20334d;
    margin: 0.5rem 0;
  }
  .report-heading .mode-tools,
  .filter-panel,
  .table-tools,
  .pagination-row,
  .risk-row,
  .student-detail .section-heading button {
    display: none !important;
  }
  .report-shell {
    margin: 0 !important;
    padding: 0 !important;
  }
  .kpi-card,
  .chart-card,
  .table-card,
  .student-detail {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
    break-inside: avoid;
  }
  .charts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  body {
    background: white !important;
  }
}
</style>
